// ProjectFormModal.tsx — Sprint 12. Unified Add/Edit modal. The
// original page had two near-identical 140-LOC modal blocks that
// only differed by a Status field and the submit button label —
// this collapses them into one definition driven by `mode`.
import React from 'react';
import {
  Button, Col, DatePicker, Form, FormInstance, Input, InputNumber, Modal, Row, Select,
} from 'antd';
import { CATEGORY_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS } from './projectTypes';

const { Option } = Select;
const { RangePicker } = DatePicker;

export type ProjectFormMode = 'add' | 'edit';

export interface ProjectFormModalProps {
  open:    boolean;
  mode:    ProjectFormMode;
  form:    FormInstance;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

/** Currency formatter / parser shared between Budget and Actual Cost
 *  fields. AntD's `formatter` callback returns string and the parser
 *  must restore a number — the inline impls in the original page
 *  duplicated this 4 times. */
const currencyFormatter = (value: number | string | undefined) =>
  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const currencyParser = (value: string | undefined) =>
  Number(value!.replace(/\$\s?|(,*)/g, '')) as any;

export const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  open, mode, form, onCancel, onSubmit,
}) => {
  const isEdit       = mode === 'edit';
  const title        = isEdit ? 'Edit Project'   : 'Add New Project';
  const submitLabel  = isEdit ? 'Update Project' : 'Add Project';

  return (
    <Modal title={title} open={open} onCancel={onCancel} footer={null} width={800}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="name" label="Project Name"
          rules={[{ required: true, message: 'Please enter project name' }]}
        >
          <Input placeholder="Enter project name" />
        </Form.Item>

        <Form.Item
          name="description" label="Description"
          rules={[{ required: true, message: 'Please enter description' }]}
        >
          <Input.TextArea rows={3} placeholder="Enter project description" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="category" label="Category"
              rules={[{ required: true, message: 'Please select category' }]}
            >
              <Select placeholder="Select category">
                {CATEGORY_OPTIONS.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="priority" label="Priority"
              rules={[{ required: true, message: 'Please select priority' }]}
            >
              <Select placeholder="Select priority">
                {PRIORITY_OPTIONS.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Status is only editable in edit mode — Add forces PLANNING (default). */}
        {isEdit && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status" label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  {STATUS_OPTIONS.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="responsiblePerson" label="Responsible Person"
                rules={[{ required: true, message: 'Please enter responsible person' }]}
              >
                <Input placeholder="Enter name of responsible person" />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="budget" label="Budget"
              rules={[{ required: true, message: 'Please enter budget' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter budget"
                min={0} step={1000} prefix="$"
                formatter={currencyFormatter as any}
                parser={currencyParser}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="actualCost" label={isEdit ? 'Actual Cost' : 'Actual Cost (Optional)'}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter actual cost"
                min={0} step={1000} prefix="$"
                formatter={currencyFormatter as any}
                parser={currencyParser}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* In Add mode, responsible-person is its own row. In Edit it
            shares the row with Status above. */}
        {!isEdit && (
          <Form.Item
            name="responsiblePerson" label="Responsible Person"
            rules={[{ required: true, message: 'Please enter responsible person' }]}
          >
            <Input placeholder="Enter name of responsible person" />
          </Form.Item>
        )}

        <Form.Item
          name="dateRange" label="Project Timeline"
          rules={[{ required: true, message: 'Please select project timeline' }]}
        >
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="notes" label="Additional Notes">
          <Input.TextArea rows={3} placeholder="Enter any additional notes" />
        </Form.Item>

        <Form.Item className="mb-0">
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">{submitLabel}</Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};
